import type { NeedsAttentionQuery } from '@crm/contracts';
import { ATTENTION_STALE_DAYS } from '@crm/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { needsAttentionApi } from '@/api/module-client';
import { queryKeys } from '@/lib/query-keys';

/**
 * The never-miss queue's key, hung off `dashboard.all` rather than given a
 * root of its own — exactly like `useFollowUpCalendar` hangs off
 * `followUps.all`. Completing a follow-up already invalidates the dashboard,
 * and this block must go stale with it or it keeps nagging about work the
 * operator has just done.
 */
export const needsAttentionKey = (params: NeedsAttentionQuery) =>
  [...queryKeys.dashboard.all, 'needs-attention', params] as const;

/**
 * The default the dashboard block and the nav badge BOTH use.
 *
 * Shared so the two cannot disagree: a nav badge saying 7 beside a block
 * listing 3 is worse than no badge, because the operator cannot tell which
 * one is lying. Sharing the object also means they share one cache entry and
 * one request.
 */
export const DEFAULT_ATTENTION_QUERY: NeedsAttentionQuery = {
  staleDays: ATTENTION_STALE_DAYS,
  mineOnly: true,
  limit: 30,
};

/**
 * Refetched on focus, like "My day": this is the tab people leave open all
 * morning, and a queue that is an hour out of date is a queue they stop
 * believing.
 */
export function useNeedsAttention(params: NeedsAttentionQuery = DEFAULT_ATTENTION_QUERY) {
  return useQuery({
    queryKey: needsAttentionKey(params),
    queryFn: () => needsAttentionApi.get(params),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    // Toggling "Mine / Everyone" dims the block rather than collapsing it to
    // a skeleton and pushing the rest of the dashboard up the page.
    placeholderData: keepPreviousData,
  });
}
